import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { Company, CompanySimilarity, WebhookCompany } from '../types/Company';
import { calculateSimilarity } from '../../shared/utils/similarity';
import { useAuth } from '../../auth/contexts/AuthContext';

interface CompanyContextType {
  companies: Company[];
  selectedCompany: Company | null;
  isLoading: boolean;
  error: string | null;
  setSelectedCompany: (company: Company | null) => void;
  addCompany: (company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Company | null>;
  updateCompany: (id: string, updates: Partial<Company>) => void;
  deleteCompany: (id: string) => void;
  removeCompany: (id: string) => void;
  getCompany: (id: string) => Company | undefined;
  checkSimilarity: (name: string) => CompanySimilarity[];
  refreshCompanies: () => Promise<void>;
  fetchCompanies: () => Promise<void>;
  downloadGlossaryFile: (company: Company) => Promise<void>;
  downloadNormalizeFile: (company: Company) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAdmin } = useAuth();

  // Webhook'tan gelen veriyi Company formatına dönüştür
  const transformWebhookCompany = (webhookCompany: WebhookCompany): Company => {
    return {
      id: webhookCompany.company_id.toString(),
      name: webhookCompany.company_name,
      createdAt: new Date(webhookCompany.company_create_date),
      updatedAt: new Date(webhookCompany.company_create_date),
      company_id: webhookCompany.company_id,
      company_name: webhookCompany.company_name,
      company_code: webhookCompany.company_code,
      company_create_date: webhookCompany.company_create_date,
      user_id: webhookCompany.user_id,
      assigned_user: webhookCompany.assigned_user,
      username: webhookCompany.username,
      created_by_username: webhookCompany.created_by_username,
      assigned_username: webhookCompany.assigned_username,
      assigned_usernames: webhookCompany.assigned_usernames,
      // Binary dosyaları ekle
      glossary_file: webhookCompany.glossary_file,
      normalize_file: webhookCompany.normalize_file,
      // Dosya adlarını belirle (varsa)
      glossaryFileName: webhookCompany.glossary_file ? `${webhookCompany.company_name}_glossary.xlsx` : undefined,
      normalizeFileName: webhookCompany.normalize_file ? `${webhookCompany.company_name}_normalize.xlsx` : undefined,
      createdBy: {
        id: webhookCompany.user_id.toString(),
        name: webhookCompany.username,
        email: undefined
      }
    };
  };

  // Webhook'tan şirket verilerini çek
  const fetchCompanies = async (): Promise<void> => {
    // User yoksa işlemi yapma
    if (!user || !user.id) {
      console.log('fetchCompanies: User henüz yüklenmedi, işlem yapılmıyor');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      console.log('fetchCompanies: Şirket verileri çekiliyor, user:', user.id);
      
      const response = await fetch('https://n8n.mahir.ai/webhook/companies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const responseText = await response.text();
        
        // Boş response kontrolü
        if (!responseText.trim()) {
          console.log('Webhook\'tan boş yanıt geldi - henüz şirket yok');
          setCompanies([]);
          localStorage.setItem('companies', JSON.stringify([]));
          return;
        }
        
        let responseData;
        try {
          responseData = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON parse hatası:', parseError);
          setError('Şirket verileri okunurken hata oluştu');
          return;
        }
        
        console.log('Webhook\'tan gelen ham veri:', responseData);
        
        // Webhook'tan gelen veriyi kontrol et
        let webhookCompanies: WebhookCompany[] = [];
        
        if (Array.isArray(responseData)) {
          // Direkt array formatında şirket listesi
          webhookCompanies = responseData;
        } else if (responseData && typeof responseData === 'object') {
          // Tek bir şirket objesi gelirse onu array'e çevir
          if (responseData.company_id && responseData.company_name) {
            webhookCompanies = [responseData];
          } else {
            console.log('Webhook\'tan veri geldi ama şirket bilgisi yok:', responseData);
            webhookCompanies = [];
          }
        } else {
          console.log('Webhook\'tan beklenmeyen format döndü:', responseData);
          webhookCompanies = [];
        }
        
        // Şirket listesi boşsa özel durum
        if (webhookCompanies.length === 0) {
          console.log('Henüz kayıtlı şirket bulunmuyor');
          setCompanies([]);
          localStorage.setItem('companies', JSON.stringify([]));
          setError(null); // Hata mesajını temizle
          return;
        }
        
        console.log('İşlenmiş şirket verileri:', webhookCompanies);
        
        // Webhook verilerini Company formatına dönüştür
        const transformedCompanies = webhookCompanies.map(transformWebhookCompany);
        
        // Admin kullanıcılar tüm şirketleri görebilir, diğerleri (editor ve redactor) sadece kendilerine atananları
        const filteredCompanies = isAdmin()
          ? transformedCompanies // Admin tüm şirketleri görür
          : user?.id 
            ? transformedCompanies.filter(company => {
                // Sadece assigned_user tanımlı ve kullanıcının ID'si ile eşleşen şirketleri göster
                if (company.assigned_user !== undefined && company.assigned_user !== null) {
                  const userId = parseInt(user.id.toString());
                  
                  // assigned_user array ise
                  if (Array.isArray(company.assigned_user)) {
                    return company.assigned_user.includes(userId);
                  }
                  
                  // assigned_user tek sayı ise
                  return userId === parseInt(company.assigned_user.toString());
                }
                // assigned_user yoksa gösterme
                return false;
              })
            : [];
        
        setCompanies(filteredCompanies);
        
        // Filtrelenmiş listeyi localStorage'a kaydet
        localStorage.setItem('companies', JSON.stringify(filteredCompanies));
        
        console.log(`Kullanıcıya atanan ${filteredCompanies.length} şirket yüklendi (Toplam: ${transformedCompanies.length})`);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Şirket verileri yüklenirken hata:', error);
      setError(error instanceof Error ? error.message : 'Bilinmeyen hata');
      
      // Hata durumunda localStorage'dan yükle
      const savedCompanies = localStorage.getItem('companies');
      if (savedCompanies) {
        try {
          const parsedCompanies = JSON.parse(savedCompanies);
          // Date objelerini yeniden oluştur
          const companiesWithDates = parsedCompanies.map((company: any) => ({
            ...company,
            createdAt: new Date(company.createdAt),
            updatedAt: new Date(company.updatedAt),
          }));
          
          // localStorage'dan yüklerken de filtrele (Admin tüm şirketleri görür, editor ve redactor sadece atananları)
          const filteredCompanies = isAdmin()
            ? companiesWithDates // Admin tüm şirketleri görür
            : user?.id 
              ? companiesWithDates.filter((company: Company) => {
                  if (company.assigned_user !== undefined && company.assigned_user !== null) {
                    const userId = parseInt(user.id.toString());
                    
                    // assigned_user array ise
                    if (Array.isArray(company.assigned_user)) {
                      return company.assigned_user.includes(userId);
                    }
                    
                    // assigned_user tek sayı ise
                    return userId === parseInt(company.assigned_user.toString());
                  }
                  // assigned_user yoksa gösterme
                  return false;
                })
              : [];
          
          setCompanies(filteredCompanies);
          console.log('Şirket verileri localStorage\'dan yüklendi ve filtrelendi');
        } catch (parseError) {
          console.error('localStorage\'dan şirket verileri okunurken hata:', parseError);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Şirket verilerini yenile
  const refreshCompanies = async (): Promise<void> => {
    await fetchCompanies();
  };

  // İlk yükleme ve user değiştiğinde şirket verilerini yükle/yenile
  useEffect(() => {
    // User yüklendiğinde şirket verilerini çek
    if (user?.id) {
      fetchCompanies();
    } else {
      // User henüz yüklenmediyse loading state'ini kaldır
      // Çünkü user yoksa zaten şirket gösteremeyiz
      setIsLoading(false);
    }
  }, [user?.id]);

  const addCompany = async (companyData: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>): Promise<Company | null> => {
    try {
      const newCompany: Company = {
        ...companyData,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        createdAt: new Date(),
        updatedAt: new Date(),
        // Add current user as createdBy if available
        createdBy: user ? {
          id: user.id || 'unknown',
          name: user.name || 'Unknown User',
          email: user.email
        } : undefined,
      };
      
      setCompanies(prev => [...prev, newCompany]);
      
      // Şirket eklendikten sonra webhook'tan güncel veriyi çek
      setTimeout(() => {
        refreshCompanies();
      }, 1000);
      
      return newCompany;
    } catch (error) {
      console.error('Error adding company:', error);
      return null;
    }
  };

  const updateCompany = (id: string, updates: Partial<Company>) => {
    setCompanies(prev => 
      prev.map(company => 
        company.id === id 
          ? { ...company, ...updates, updatedAt: new Date() }
          : company
      )
    );
    
    // Güncelleme sonrası webhook'tan güncel veriyi çek
    setTimeout(() => {
      refreshCompanies();
    }, 1000);
  };

  const deleteCompany = async (id: string) => {
    try {
      // Silinecek şirketi bul
      const companyToDelete = companies.find(company => company.id === id);
      
      if (companyToDelete && user) {
        // Webhook'a silme bilgilerini gönder
        const response = await fetch('https://n8n.mahir.ai/webhook/delete_company', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyToDelete.company_id || companyToDelete.id
          })
        });

        if (response.ok) {
          console.log('Şirket silme webhook\'ı başarıyla gönderildi');
        } else {
          console.error('Webhook gönderilirken hata:', response.status, response.statusText);
        }
      }
    } catch (error) {
      console.error('Şirket silme webhook\'ı gönderilirken hata:', error);
    }

    // UI'dan şirketi kaldır
    setCompanies(prev => prev.filter(company => company.id !== id));
    // Eğer silinen company seçili ise, selectedCompany'yi temizle
    if (selectedCompany?.id === id) {
      setSelectedCompany(null);
    }
    
    // Silme sonrası webhook'tan güncel veriyi çek
    setTimeout(() => {
      refreshCompanies();
    }, 1000);
  };

  const removeCompany = (id: string) => {
    deleteCompany(id);
  };

  const getCompany = (id: string): Company | undefined => {
    return companies.find(company => company.id === id);
  };

  const checkSimilarity = (name: string): CompanySimilarity[] => {
    const similarities: CompanySimilarity[] = [];
    
    companies.forEach(company => {
      const similarity = calculateSimilarity(name, company.name);
      if (similarity >= 50) { // 50% ve üzeri benzerlik oranları döndür
        similarities.push({
          company,
          similarity
        });
      }
    });
    
    return similarities.sort((a, b) => b.similarity - a.similarity);
  };

  const downloadGlossaryFile = async (company: Company) => {
    try {
      const company_id = company.company_id || company.id;
      
      console.log('downloadGlossaryFile çağrıldı, company_id:', company_id);
      console.log('get_glossary endpoint\'e istek gönderiliyor...');
      
      const response = await fetch('https://n8n.mahir.ai/webhook/get_glossary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: company_id
        })
      });
      
      console.log('get_glossary endpoint response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      let filename = company.glossaryFileName || `${company.name}_glossary.xlsx`;
      let blob;
      
      // Content-Type'a göre response'ı işle
      const contentType = response.headers.get('content-type');
      
      if (contentType && (contentType.includes('application/json') || contentType.includes('text/json'))) {
        // JSON response
        const responseData = await response.json();
        
        filename = responseData.filename || filename;
        const fileData = responseData.file || responseData.data;
        
        if (!fileData) {
          throw new Error('JSON response\'da dosya verisi bulunamadı');
        }
        
        // Base64 string'i Blob'a çevir
        if (typeof fileData === 'string') {
          const byteCharacters = atob(fileData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        } else {
          blob = new Blob([fileData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        }
      } else {
        // Binary response (Excel, PDF, etc.)
        blob = await response.blob();
        
        // Excel dosyası için doğru MIME type'ı ayarla
        if (contentType && (contentType.includes('application/x-cfb') || contentType.includes('application/vnd.ms-excel'))) {
          blob = new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        }
        
        // Header'dan filename almaya çalış
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Glossary dosyası indirilirken hata:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        company_id: company.company_id || company.id
      });
      throw error;
    }
  };

  const downloadNormalizeFile = async (company: Company) => {
    try {
      const company_id = company.company_id || company.id;
      
      const response = await fetch('https://n8n.mahir.ai/webhook/get_normalize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: company_id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      let filename = company.normalizeFileName || `${company.name}_normalize.xlsx`;
      let blob;
      
      // Content-Type'a göre response'ı işle
      const contentType = response.headers.get('content-type');
      
      if (contentType && (contentType.includes('application/json') || contentType.includes('text/json'))) {
        // JSON response
        const responseData = await response.json();
        
        filename = responseData.filename || filename;
        const fileData = responseData.file || responseData.data;
        
        if (!fileData) {
          throw new Error('JSON response\'da dosya verisi bulunamadı');
        }
        
        // Base64 string'i Blob'a çevir
        if (typeof fileData === 'string') {
          const byteCharacters = atob(fileData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        } else {
          blob = new Blob([fileData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        }
      } else {
        // Binary response (Excel, PDF, etc.)
        blob = await response.blob();
        
        // Excel dosyası için doğru MIME type'ı ayarla
        if (contentType && (contentType.includes('application/x-cfb') || contentType.includes('application/vnd.ms-excel'))) {
          blob = new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        }
        
        // Header'dan filename almaya çalış
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Normalize dosyası indirilirken hata:', error);
      console.error('Normalize Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        company_id: company.company_id || company.id
      });
      throw error;
    }
  };

  return (
    <CompanyContext.Provider value={{
      companies,
      selectedCompany,
      isLoading,
      error,
      setSelectedCompany,
      addCompany,
      updateCompany,
      deleteCompany,
      removeCompany,
      getCompany,
      checkSimilarity,
      refreshCompanies,
      fetchCompanies,
      downloadGlossaryFile,
      downloadNormalizeFile
    }}>
      {children}
    </CompanyContext.Provider>
  );
};
